import { z } from 'zod';
import { amountSchema, percentSchema } from './units';

const samplingSeedSchema = z.string();

const samplingSizeSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('DrawUniqueSamplesWithATotalAmountAbove'),
    data: amountSchema,
  }),
  z.object({
    type: z.literal('DrawUniqueSamples'),
    data: z.number(),
  }),
]);
type SamplingSize = z.infer<typeof samplingSizeSchema>;

const samplingDistributionSchema = z.union([z.literal('Uniform'), z.literal('WeightedByAmount')]);
type SamplingDistribution = z.infer<typeof samplingDistributionSchema>;

const _samplingKindSchema = z.union([
  z.literal('OnlyLargest'),
  z.literal('OnlyStatistical'),
  z.literal('LargestAndStatistical'),
]);
type SamplingKind = z.infer<typeof _samplingKindSchema>;

const statisticalBasisSchema = z.object({
  confidenceFactor: z.number(),
  confidenceLevel: percentSchema,
  acceptableFault: amountSchema.optional(),
});
type StatisticalBasis = z.infer<typeof statisticalBasisSchema>;

const onlyLargestSchema = z.object({
  type: z.literal('OnlyLargest'),
  largest: samplingSizeSchema,
});
const onlyStatisticalSchema = z.object({
  type: z.literal('OnlyStatistical'),
  statistical: samplingSizeSchema,
  statisticalBasis: statisticalBasisSchema.optional(),
  distribution: samplingDistributionSchema,
});
const largestAndStatisticalSchema = z.object({
  type: z.literal('LargestAndStatistical'),
  largest: samplingSizeSchema,
  statistical: samplingSizeSchema,
  statisticalBasis: statisticalBasisSchema.optional(),
  distribution: samplingDistributionSchema,
});

const samplingStrategySchema = z.discriminatedUnion('type', [
  onlyLargestSchema,
  onlyStatisticalSchema,
  largestAndStatisticalSchema,
]);

const samplingStrategyWithLargestSchema = z.discriminatedUnion('type', [
  onlyLargestSchema,
  largestAndStatisticalSchema,
]);
type SamplingWithLargestStrategy = z.infer<typeof samplingStrategyWithLargestSchema>;

const samplingWithStatisticalStrategySchema = z.discriminatedUnion('type', [
  onlyStatisticalSchema,
  largestAndStatisticalSchema,
]);
type SamplingWithStatisticalStrategy = z.infer<typeof samplingWithStatisticalStrategySchema>;

function withLargestStrategy(strategy: unknown): SamplingWithLargestStrategy | undefined {
  return samplingStrategyWithLargestSchema.safeParse(strategy).data;
}

function withStatisticalStrategy(strategy: unknown): SamplingWithStatisticalStrategy | undefined {
  return samplingWithStatisticalStrategySchema.safeParse(strategy).data;
}

const samplingPopulationSignSchema = z.union([
  z.literal('All'),
  z.literal('Debit'),
  z.literal('Credit'),
]);
type SamplingPopulationSign = z.infer<typeof samplingPopulationSignSchema>;

const samplingConfigurationSchema = z.object({
  strategy: samplingStrategySchema,
  populationSign: samplingPopulationSignSchema,
});
type SamplingConfiguration = z.infer<typeof samplingConfigurationSchema>;

export {
  samplingConfigurationSchema,
  samplingSeedSchema,
  withLargestStrategy,
  withStatisticalStrategy,
  type SamplingConfiguration,
  type SamplingDistribution,
  type SamplingKind,
  type SamplingPopulationSign,
  type SamplingSize,
  type StatisticalBasis,
};
